import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { FiChevronRight } from 'react-icons/fi';
import { CredusButton, CredusText, color } from 'ui';
import { Divider } from 'antd';

import CandidateUploadTable from './tables/CandidateUploadTable';
import ConfirmAction from './modals/ConfirmAction';
import { CandidateContext } from '../../../../contexts/CandidateContext';
import { ProceedIcon, ReUploadIcon } from '../../../../assets/images/svgs';

interface BulletProps {
  color?: string;
  background?: string;
}

interface CandidateReUploadProps {
  setCandidateReUpload: React.Dispatch<React.SetStateAction<boolean>>;
  setReviewUpload: React.Dispatch<React.SetStateAction<boolean>>;
  setShowImportCandidate: React.Dispatch<React.SetStateAction<boolean>>;
}

const CandidateReUpLoad = ({
  setCandidateReUpload,
  setShowImportCandidate,
  setReviewUpload,
}: CandidateReUploadProps) => {
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>(['']);
  const [confirmation, setConfirmation] = useState(false);
  const [text, setText] = useState('');
  const [confirmFunction, setConfirmFunction] = useState<() => void>();
  const { candidateData, candidateCategory, setCandidateData } =
    useContext(CandidateContext);

  const deleteAllQuestions = () => {
    setCandidateData((prev) => ({ ...prev, Candidates: [] }));
    setConfirmation(false);
  };

  const reUploadQuestions = () => {
    setCandidateReUpload(false);
    setShowImportCandidate(true);
    setConfirmation(false);
  };

  const handleSelectAll = () => {
    const testCandidates = candidateData.Candidates.map((el) => el.regNo);
    setSelectedCandidates((prev) =>
      prev.length == testCandidates.length ? [] : [...testCandidates]
    );
  };

  const handleDelete = () => {
    setConfirmation(true);
    setText(
      'You are about to delete all candidates, your current data would be lost. Do you want to continue?'
    );
    setConfirmFunction(() => deleteAllQuestions);
  };
  const handleReUpload = () => {
    setConfirmation(true);
    setText(
      'You are about to re-upload your candidates, your current data would be lost. Do you want to continue?'
    );

    setConfirmFunction(() => reUploadQuestions);
  };

  const handleProceed = () => {
    setCandidateReUpload(false);
    setReviewUpload(true);
  };

  return (
    <Container>
      {confirmation && (
        <ConfirmAction
          showActionConfirmation={confirmation}
          setShowActionConfirmation={setConfirmation}
          text={text}
          action={confirmFunction}
        />
      )}
      <Header>
        <BulletDiv>
          <Bullet color={color.white} background={color.primary}>
            <CredusText size="xs" color={color.white}>
              1
            </CredusText>
          </Bullet>
          <CredusText size="md">Upload</CredusText>
        </BulletDiv>
        <FiChevronRight color="#000929" />
        <BulletDiv>
          <Bullet background={color.lightPurple}>
            <CredusText size="xs">2</CredusText>
          </Bullet>
          <CredusText size="md">Review & Publish</CredusText>
        </BulletDiv>
      </Header>

      <SubHeader>
        <CredusText size="md">
          Exam category: <strong>{candidateCategory.category}</strong>
        </CredusText>
        <Divider
          style={{ height: '20px', background: color.lightPurple }}
          type="vertical"
        />

        <CredusText size="md">
          Period: <strong> {candidateCategory.period}</strong>
        </CredusText>
        <Divider
          type="vertical"
          style={{ height: '20px', background: color.lightPurple }}
        />
        <CredusText size="md">
          Status: <strong> In progress</strong>
        </CredusText>
      </SubHeader>
      <NoteDiv>
        <CredusText size="sm">Note:</CredusText>
        <ul>
          <li>
            <CredusText size="xs">
              Always check the format requirement properly before uploading and
              make sure you confirm every information so as not to make mistakes
              before uploading.
            </CredusText>
          </li>
        </ul>
      </NoteDiv>
      <BtnDiv>
        <InnerBtnDiv>
          <CredusButton
            outlined
            onClick={handleSelectAll}
            disabled={candidateData.Candidates.length == 0}
          >
            Select All
          </CredusButton>
          <CredusButton
            onClick={handleDelete}
            outlined
            disabled={
              !(
                candidateData.Candidates.map((el) => el.regNo).length ==
                selectedCandidates.length
              )
            }
          >
            Delete All
          </CredusButton>
          <CredusButton
            onClick={handleReUpload}
            outlined
            preffix={<ReUploadIcon />}
          >
            Re-upload
          </CredusButton>
        </InnerBtnDiv>
        <InnerBtnDiv>
          <CredusButton
            onClick={handleProceed}
            suffix={<ProceedIcon />}
            disabled={candidateData.Candidates.length == 0}
          >
            Proceed
          </CredusButton>
        </InnerBtnDiv>
      </BtnDiv>
      <CandidateUploadTable
        candidateData={candidateData}
        setCandidateData={setCandidateData}
        selectedCandidates={selectedCandidates}
        setSelectedCandidates={setSelectedCandidates}
      />
    </Container>
  );
};

export default CandidateReUpLoad;

const Container = styled.div`
  background: ${color.white};
  padding: 2rem 1rem;
`;
const Header = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const BulletDiv = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Bullet = styled.div<BulletProps>`
  height: 28px;
  width: 28px;
  border-radius: 50%;
  background: ${(props) => props.background};
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding-top: 3.5rem;
  width: 100%;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(0, 9, 41, 0.15);
`;

const NoteDiv = styled.div`
  width: 100%;
  min-height: 108px;
  background: #f8f8f8;
  padding: 1rem 1.5rem;
  margin-top: 1.5rem;
`;

const BtnDiv = styled.div`
  width: 100%;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
`;
const InnerBtnDiv = styled.div`
  display: flex;
  gap: 1rem;
`;
